import PropTypes from 'prop-types';

/**
 * Formulario estandarizado para operaciones CRUD
 * Proporciona campos de formulario consistentes y validación
 */
const StandardForm = ({
  id,
  fields,
  data,
  onChange,
  onSubmit,
  errors = {},
  loading = false,
  submitLabel = 'Guardar',
  cancelLabel = 'Cancelar',
  onCancel,
  className = '',
  layout = 'vertical',
  mode = 'create' // 'create' o 'edit'
}) => {
  // Función para renderizar un campo individual
  const renderField = (field) => {
    const {
      key,
      label,
      type = 'text',
      placeholder,
      required = false,
      disabled = false,
      options = [],
      validation: _validation = {},
      className: fieldClassName = '',
      rows = 3,
      accept,
      multiple = false,
      min,
      max,
      step
    } = field;

    // Manejar campos anidados (ej: ageRange.min)
    let value = '';
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      value = data[parent]?.[child] || '';
    } else {
      value = data[key] || '';
    }

    const error = errors[key];
    const fieldId = `field-${key}`;

    const commonProps = {
      id: fieldId,
      name: key,
      value,
      onChange: (e) => onChange(key, e.target.value, e),
      disabled: disabled || loading,
      className: `form-input ${error ? 'error' : ''} ${fieldClassName}`,
      'aria-describedby': error ? `${fieldId}-error` : undefined,
      'aria-invalid': !!error
    };

    let inputElement;

    switch (type) {
      case 'textarea':
        inputElement = (
          <textarea
            {...commonProps}
            placeholder={placeholder}
            rows={rows}
            required={required}
          />
        );
        break;

      case 'select':
        inputElement = (
          <select {...commonProps} required={required}>
            <option value="">{placeholder || 'Seleccionar...'}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
        break;

      case 'checkbox':
        inputElement = (
          <label className="checkbox-container">
            <input
              type="checkbox"
              name={key}
              checked={!!value}
              onChange={(e) => onChange(key, e.target.checked, e)}
              disabled={disabled || loading}
              className={`form-checkbox ${error ? 'error' : ''}`}
            />
            <span className="checkmark"></span>
            <span className="checkbox-label">{label}</span>
          </label>
        );
        break;

      case 'radio':
        inputElement = (
          <div className="radio-group">
            {options.map((option) => (
              <label key={option.value} className="radio-container">
                <input
                  type="radio"
                  name={key}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => onChange(key, e.target.value, e)}
                  disabled={disabled || loading}
                  className={`form-radio ${error ? 'error' : ''}`}
                />
                <span className="radio-mark"></span>
                <span className="radio-label">{option.label}</span>
              </label>
            ))}
          </div>
        );
        break;

      case 'file':
        inputElement = (
          <input
            type="file"
            {...commonProps}
            accept={accept}
            multiple={multiple}
            onChange={(e) => onChange(key, e.target.files, e)}
            value={undefined} // File inputs can't have controlled values
          />
        );
        break;

      case 'password-with-controls':
        inputElement = (
          <div className="password-field-container">
            <div className="password-input-group">
              <input
                type={field.showPassword ? 'text' : 'password'}
                {...commonProps}
                placeholder={placeholder}
                required={required}
                readOnly={field.readOnly}
              />
              <div className="password-controls">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => field.onToggleVisibility && field.onToggleVisibility()}
                  title={field.showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {field.showPassword ? '👁️‍🗨️' : '👁️'}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => field.onRegenerate && field.onRegenerate()}
                  title="Generar nueva contraseña"
                >
                  🔄
                </button>
              </div>
            </div>
          </div>
        );
        break;

      case 'number':
        inputElement = (
          <input
            type="number"
            {...commonProps}
            placeholder={placeholder}
            required={required}
            min={min}
            max={max}
            step={step}
          />
        );
        break;

      case 'email':
        inputElement = (
          <input
            type="email"
            {...commonProps}
            placeholder={placeholder}
            required={required}
          />
        );
        break;

      case 'password':
        inputElement = (
          <input
            type="password"
            {...commonProps}
            placeholder={placeholder}
            required={required}
          />
        );
        break;

      case 'date':
        inputElement = (
          <input
            type="date"
            {...commonProps}
            required={required}
          />
        );
        break;

      case 'time':
        inputElement = (
          <input
            type="time"
            {...commonProps}
            required={required}
          />
        );
        break;

      case 'url':
        inputElement = (
          <input
            type="url"
            {...commonProps}
            placeholder={placeholder}
            required={required}
          />
        );
        break;

      default:
        inputElement = (
          <input
            type="text"
            {...commonProps}
            placeholder={placeholder}
            required={required}
          />
        );
    }

    return (
      <div key={key} className={`form-group ${type === 'checkbox' ? 'checkbox-group' : ''}`}>
        {type !== 'checkbox' && (
          <label htmlFor={fieldId} className="form-label">
            {label}
            {required && <span className="required-indicator">*</span>}
          </label>
        )}

        {inputElement}

        {error && (
          <div id={`${fieldId}-error`} className="form-error" role="alert">
            <span className="material-icons">error</span>
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <form
      id={id}
      onSubmit={onSubmit}
      className={`standard-form standard-form-${layout} ${className}`}
      noValidate
    >
      <div className="form-fields">
        {fields
          .filter(field => {
            // Filtrar campos según el modo
            if (mode === 'create' && field.showInEdit && !field.showInCreate) return false;
            if (mode === 'edit' && field.showInCreate && !field.showInEdit) return false;
            return true;
          })
          .map(renderField)}
      </div>

      {/* Solo mostrar acciones si hay labels definidos */}
      {(submitLabel || cancelLabel || onCancel) && (
        <div className="form-actions">
          {onCancel && cancelLabel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="btn btn-secondary"
            >
              {cancelLabel}
            </button>
          )}

          {submitLabel && (
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? (
                <>
                  <span className="btn-spinner"></span>
                  Guardando...
                </>
              ) : (
                submitLabel
              )}
            </button>
          )}
        </div>
      )}
    </form>
  );
};

StandardForm.propTypes = {
  id: PropTypes.string,
  fields: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.oneOf([
      'text', 'textarea', 'select', 'checkbox', 'radio', 'file',
      'number', 'email', 'password', 'date', 'time', 'url', 'password-with-controls'
    ]),
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })),
    validation: PropTypes.object,
    className: PropTypes.string,
    rows: PropTypes.number,
    accept: PropTypes.string,
    multiple: PropTypes.bool,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number
  })).isRequired,
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  errors: PropTypes.object,
  loading: PropTypes.bool,
  submitLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  onCancel: PropTypes.func,
  className: PropTypes.string,
  layout: PropTypes.oneOf(['vertical', 'horizontal', 'inline']),
  mode: PropTypes.oneOf(['create', 'edit'])
};

export default StandardForm;
